/**
 * <g-column> - Kanban Column
 *
 * A single column in a kanban board with header and droppable card area.
 *
 * @example
 * <g-column status="todo" label="To Do" sublabel="3 tasks" color="purple">
 *   <g-card card-id="1">...</g-card>
 * </g-column>
 *
 * @example Header with action buttons
 * <g-column status="todo" label="To Do" color="purple">
 *   <button slot="header-action" class="btn btn-xs btn-ghost btn-circle">+</button>
 *   <g-card card-id="1">...</g-card>
 * </g-column>
 *
 * @attr {string} status - Status value for cards in this column
 * @attr {string} label - Display label in header
 * @attr {string} color - Top border color: purple, blue, orange, green, cyan, or hex value
 * @attr {string} sublabel - Optional sublabel (e.g., "3 deals · $450K")
 * @slot header-action - Optional buttons/icons displayed in the header (right side)
 */
export class GColumn extends HTMLElement {
  static get observedAttributes() {
    return ['label', 'color', 'sublabel'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback(name) {
    if (!this.isConnected) return;

    if (name === 'color') {
      this.updateColor();
    }
    this.updateHeader();
  }

  updateColor() {
    const colorAttr = this.getAttribute('color');
    const color = colorAttr ? this.getColor(colorAttr) : null;
    if (color) {
      this.style.borderTop = `4px solid ${color}`;
    } else {
      this.style.borderTop = '';
    }
  }

  getColor(color) {
    const colors = {
      purple: '#8b5cf6',
      blue: '#3b82f6',
      cyan: '#06b6d4',
      orange: '#f97316',
      green: '#22c55e',
      red: '#ef4444',
      pink: '#ec4899',
      yellow: '#eab308',
      gray: '#6b7280'
    };
    return colors[color] || color || colors.gray;
  }

  render() {
    const colorAttr = this.getAttribute('color');
    const color = colorAttr ? this.getColor(colorAttr) : null;

    // Use Tailwind classes for styling
    // Note: width (w-[300px] or flex-1) is controlled by parent g-kanban
    this.classList.add(
      'flex', 'flex-col', 'shrink-0', 'rounded-xl', 'p-3', 'pt-0',
      'bg-base-200', 'overflow-hidden'
    );

    // Dynamic colored top border (only style that must be inline)
    if (color) {
      this.style.borderTop = `4px solid ${color}`;
    }

    // Create header
    this.updateHeader();
  }

  updateHeader() {
    const label = this.getAttribute('label') || '';
    const sublabel = this.getAttribute('sublabel') || '';

    let header = this.querySelector('.g-column-header');

    // Preserve existing action elements before rebuilding
    const existingActions = header?.querySelectorAll('.g-column-actions > *');
    const savedActions = existingActions ? [...existingActions] : [];

    if (!header) {
      header = document.createElement('div');
      this.insertBefore(header, this.firstChild);
    }

    header.className = 'g-column-header flex justify-between items-start py-3 px-1 mb-1';

    header.innerHTML = `
      <div>
        <div class="font-semibold text-base-content">${label}</div>
        ${sublabel ? `<div class="text-xs text-base-content/50 mt-0.5">${sublabel}</div>` : ''}
      </div>
      <div class="g-column-actions flex gap-1"></div>
    `;

    const actionsContainer = header.querySelector('.g-column-actions');

    // Restore saved actions, or collect new ones on first render
    if (savedActions.length > 0) {
      savedActions.forEach(el => actionsContainer.appendChild(el));
    } else {
      const newActions = this.querySelectorAll('[slot="header-action"]');
      newActions.forEach(el => actionsContainer.appendChild(el));
    }
  }
}

customElements.define('g-column', GColumn);
